import React from 'react';
import { Helmet } from 'react-helmet-async';

// Site URL constant - always use non-www version
const SITE_URL = 'https://mysummersquad.com';

/**
 * SEO Component for dynamic meta tags and structured data
 * Usage:
 * <SEO 
 *   title="Page Title" 
 *   description="Page description" 
 *   keywords="keyword1, keyword2"
 *   canonical="https://mysummersquad.com/page" // Optional: auto-generated if not provided
 *   canonicalUrl="https://mysummersquad.com/page" // Deprecated: use 'canonical' instead
 * />
 */
const SEO = ({ 
  title = 'Summer Squad - AI-Powered Summer Camp Planning',
  description = 'Empowering parents with intelligent tools that help you discover, coordinate, and manage your family\'s summer camp adventures - all in one place. Plan smarter, not harder.',
  keywords = 'summer camp planning, AI camp assistant, camp timeline, summer activities, family planning, AI email reader, camp discovery, summer programs',
  canonical, // New prop name
  canonicalUrl, // Legacy prop name for backward compatibility
  ogTitle,
  ogDescription,
  ogImage = 'https://mysummersquad.com/images/Summer%20Squad%20Site%20Image.png',
  ogType = 'website',
  twitterTitle,
  twitterDescription,
  noindex = false,
  // Blog post specific props
  type = 'website', // 'article' for blog posts
  publishedTime,
  modifiedTime,
  author,
  // Custom structured data (e.g. LocalBusiness for program/place pages); when provided, used instead of default
  structuredData: customStructuredData,
  // Optional BreadcrumbList: array of { name, url } for JSON-LD BreadcrumbList
  breadcrumbList,
  // Optional FAQPage schema: array of { question, answer } for JSON-LD FAQPage
  faqSchema
}) => {
  /**
   * Get the canonical URL for the current page
   * - Uses provided canonical if available (supports full URLs or relative paths)
   * - Otherwise auto-generates from current page path (CRITICAL: current path, not homepage)
   * - Always normalizes to non-www version
   */
  const getCanonicalUrl = () => {
    // If canonical prop is provided, use it
    const providedCanonical = canonical || canonicalUrl;
    if (providedCanonical) {
      return providedCanonical.startsWith('http')
        ? providedCanonical
        : `${SITE_URL}${providedCanonical.startsWith('/') ? providedCanonical : '/' + providedCanonical}`;
    }

    // Auto-generate from current page path (never return homepage for deep links)
    if (typeof window !== 'undefined') {
      const currentPath = window.location.pathname;
      const cleanPath = currentPath.endsWith('/') && currentPath !== '/'
        ? currentPath.slice(0, -1)
        : currentPath;
      return `${SITE_URL}${cleanPath}`;
    }

    // Fallback to homepage only when no path available (e.g. SSR)
    return SITE_URL;
  };

  // Normalize canonical URL to always use non-www version
  // This ensures both www and non-www versions point to the same canonical URL
  const normalizeCanonicalUrl = (url) => {
    if (!url) return url;
    // Remove www. from the URL to ensure consistent canonical tags
    return url.replace(/https?:\/\/www\./, 'https://');
  };

  const canonicalUrlValue = getCanonicalUrl();
  const normalizedCanonicalUrl = normalizeCanonicalUrl(canonicalUrlValue);

  // Use specific titles/descriptions if provided, otherwise fall back to defaults
  const finalOgTitle = ogTitle || title;
  const finalOgDescription = ogDescription || description;
  const finalTwitterTitle = twitterTitle || title;
  const finalTwitterDescription = twitterDescription || description;

  // Generate blog post schema for articles
  const generateBlogPostSchema = () => {
    if (type !== 'article' || !publishedTime) return null;
    
    return {
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      headline: title,
      description: description,
      image: ogImage,
      datePublished: publishedTime,
      dateModified: modifiedTime || publishedTime,
      author: {
        '@type': 'Person',
        name: author || 'Summer Squad Team',
      },
      publisher: {
        '@type': 'Organization',
        name: 'Summer Squad',
        logo: {
          '@type': 'ImageObject',
          url: 'https://mysummersquad.com/images/logo.png',
        },
      },
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': normalizedCanonicalUrl,
      },
    };
  };

  // Structured data for SoftwareApplication (default)
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'Summer Squad',
    applicationCategory: 'WebApplication',
    operatingSystem: 'Web',
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'USD',
      availability: 'https://schema.org/InStock',
      description: 'Free to start with optional Premium features'
    },
    description: 'AI-powered summer camp planning platform with three assistants: email reader, timeline builder, and program discoverer',
    featureList: [
      'AI Email Scout - Reads camp emails automatically',
      'AI Planning Coach - Builds timelines and detects conflicts',
      'AI Perfect Match - Discovers hidden gem programs',
      'Social planning with your squad'
    ],
    image: ogImage,
    screenshot: ogImage,
    url: 'https://mysummersquad.com',
    creator: {
      '@type': 'Organization',
      name: 'Prism Digital Labs',
      url: 'https://mysummersquad.com'
    }
  };

  // Use custom structured data when provided (e.g. LocalBusiness for program/place pages), else blog post or default
  const finalStructuredData = customStructuredData && typeof customStructuredData === 'object'
    ? customStructuredData
    : (type === 'article' && publishedTime ? generateBlogPostSchema() : structuredData);

  return (
    <Helmet>
      {/* Favicons - Ensure correct logo is displayed */}
      <link rel="icon" type="image/png" sizes="32x32" href="/favicon.png" />
      <link rel="icon" type="image/png" sizes="192x192" href="/favicon.png" />
      <link rel="shortcut icon" type="image/png" href="/favicon.png" />
      <link rel="icon" type="image/svg+xml" href="/favicon.svg" />
      <link rel="apple-touch-icon" sizes="180x180" href="/apple-touch-icon.png" />
      
      {/* Primary Meta Tags */}
      <title>{title}</title>
      <meta name="title" content={title} />
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      {/* CRITICAL: Canonical must point to current page, not homepage */}
      {normalizedCanonicalUrl && <link rel="canonical" href={normalizedCanonicalUrl} />}
      
      {/* Robots Meta */}
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={normalizedCanonicalUrl} />
      <meta property="og:title" content={finalOgTitle} />
      <meta property="og:description" content={finalOgDescription} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />

      {/* Twitter Card */}
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:url" content={normalizedCanonicalUrl} />
      <meta property="twitter:title" content={finalTwitterTitle} />
      <meta property="twitter:description" content={finalTwitterDescription} />
      <meta property="twitter:image" content={ogImage} />
      <meta property="twitter:image:width" content="1200" />
      <meta property="twitter:image:height" content="630" />

      {/* Structured Data */}
      {finalStructuredData && (
        <script type="application/ld+json">
          {JSON.stringify(finalStructuredData)}
        </script>
      )}
      {/* BreadcrumbList JSON-LD when provided */}
      {breadcrumbList && Array.isArray(breadcrumbList) && breadcrumbList.length > 0 && (
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: breadcrumbList.map((item, index) => ({
              '@type': 'ListItem',
              position: index + 1,
              name: item.name,
              item: item.url && item.url.startsWith('http') ? item.url : `${SITE_URL}${(item.url || '').startsWith('/') ? item.url : '/' + (item.url || '')}`,
            })),
          })}
        </script>
      )}
      {/* FAQPage JSON-LD when provided */}
      {faqSchema && Array.isArray(faqSchema) && faqSchema.length > 0 && (
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: faqSchema.map((item) => ({
              '@type': 'Question',
              name: item.question,
              acceptedAnswer: {
                '@type': 'Answer',
                text: item.answer,
              },
            })),
          })}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;

